import { supabase } from '@/lib/supabase';
import { reindexOne } from '@/lib/coreSearch';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Socle multi-tenant
// Fournit les entités communes à tous les modules :
// Tenant → Organization → Locations / Addresses / Memberships.
// Le "legacy" (tables users / userwebsite / userrole) reste la source
// d'identité ; le Core s'y rattache via legacy_user_id / legacy_commerce_id.
// ─────────────────────────────────────────────────────────────

export interface CoreTenant {
  id: number;
  name: string;
  slug: string | null;
  owner_user_id: number | null;
  legacy_commerce_id: number | null;
  plan: number | null;
  status: string;
  default_currency: string | null;
  default_language: string | null;
  default_country: string | null;
  timezone: string | null;
  created_at: string | null;
}

export interface CoreOrganization {
  id: number;
  tenant_id: number | null;
  owner_user_id: number | null;
  legacy_user_id: number | null;
  legal_name: string | null;
  commercial_name: string | null;
  business_type: string | null;
  activity_category: string | null;
  activity_subcategory: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  website: string | null;
  logo: string | null;
  description: string | null;
  country: string | null;
  default_currency: string | null;
  timezone: string | null;
  language: string | null;
  status: string;
  created_at: string | null;
}

export interface CoreLocation {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  is_primary: boolean | null;
  address_id: number | null;
  phone: string | null;
  manager_user_id: number | null;
  latitude: number | null;
  longitude: number | null;
  status: string;
  created_at: string | null;
  core_organizations?: { id: number; commercial_name: string | null } | null;
  core_addresses?: {
    id: number;
    city: string | null;
    country: string | null;
    address_line_1: string | null;
    address_line_2?: string | null;
    region?: string | null;
    postal_code?: string | null;
  } | null;
}

export interface CoreAddress {
  id: number;
  tenant_id: number | null;
  owner_type: string;
  owner_id: number | null;
  label: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  is_default: boolean | null;
  created_at: string | null;
}

export interface CoreMembership {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number;
  role: string;
  role_label: string | null;
  role_id?: number | null;
  permissions?: string[] | null;
  status: string;
  is_primary: boolean | null;
  joined_at: string | null;
  core_organizations?: { id: number; commercial_name: string | null } | null;
}

export interface UserLabel {
  id: number;
  name: string | null;
  user_name: string | null;
  email: string | null;
}

// ── Lectures ──

export async function listCoreTenants(): Promise<CoreTenant[]> {
  const { data, error } = await supabase
    .from('core_tenants')
    .select('*')
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreTenant[];
}

export async function listCoreOrganizations(): Promise<CoreOrganization[]> {
  const { data, error } = await supabase
    .from('core_organizations')
    .select('*')
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreOrganization[];
}

export async function listCoreLocations(): Promise<CoreLocation[]> {
  const { data, error } = await supabase
    .from('core_locations')
    .select('*, core_organizations(id, commercial_name), core_addresses(id, city, country, address_line_1)')
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as CoreLocation[];
}

export async function listCoreAddresses(): Promise<CoreAddress[]> {
  const { data, error } = await supabase
    .from('core_addresses')
    .select('*')
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreAddress[];
}

export async function listCoreMemberships(): Promise<CoreMembership[]> {
  const { data, error } = await supabase
    .from('core_memberships')
    .select('*, core_organizations(id, commercial_name)')
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as CoreMembership[];
}

/** Récupère les libellés (nom / pseudo / email) des utilisateurs par ids. */
export async function fetchUserLabels(ids: number[]): Promise<Record<number, UserLabel>> {
  const unique = Array.from(new Set(ids.filter((n) => Number.isFinite(n) && n > 0)));
  if (unique.length === 0) return {};
  const { data, error } = await supabase
    .from('users')
    .select('id, name, user_name, email')
    .in('id', unique);
  if (error) return {};
  const map: Record<number, UserLabel> = {};
  (data || []).forEach((row) => {
    const r = row as unknown as UserLabel;
    map[r.id] = r;
  });
  return map;
}

// ── Provisionnement / liaison (idempotent, best-effort) ──

export interface CoreProvisionInput {
  userId: number;
  commercialName?: string | null;
  displayName?: string | null;
  userName?: string | null;
  businessType?: string | null;
  activityCategory?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  logo?: string | null;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  currency?: string | null;
  language?: string | null;
}

function slugify(value: string | null | undefined, fallback: string): string {
  const base = (value || '').trim().toLowerCase();
  const slug = base.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || fallback;
}

/**
 * Crée le socle Core d'un marchand (tenant + organisation + établissement +
 * adresse + membership propriétaire). Idempotent : ne fait rien si l'organisation
 * existe déjà pour ce user. N'échoue jamais l'appelant (best-effort).
 */
export async function provisionCoreForMerchant(
  input: CoreProvisionInput,
): Promise<{ tenantId: number | null; organizationId: number | null }> {
  try {
    const { data: existingOrg } = await supabase
      .from('core_organizations')
      .select('id, tenant_id')
      .eq('legacy_user_id', input.userId)
      .maybeSingle();
    if (existingOrg) {
      return { tenantId: (existingOrg.tenant_id as number) ?? null, organizationId: existingOrg.id as number };
    }

    const currency = (input.currency || 'MAD').toUpperCase();
    const displayName = input.commercialName || input.displayName || `Commerce ${input.userId}`;

    const { data: tenant, error: tErr } = await supabase
      .from('core_tenants')
      .insert({
        name: displayName,
        slug: slugify(input.userName, `commerce-${input.userId}`),
        owner_user_id: input.userId,
        legacy_commerce_id: input.userId,
        status: 'active',
        default_currency: currency,
        default_language: input.language || 'fr',
        default_country: input.country || null,
      })
      .select('id')
      .maybeSingle();
    if (tErr || !tenant) return { tenantId: null, organizationId: null };

    const { data: org, error: oErr } = await supabase
      .from('core_organizations')
      .insert({
        tenant_id: tenant.id,
        owner_user_id: input.userId,
        legacy_user_id: input.userId,
        commercial_name: input.commercialName || input.displayName || null,
        business_type: input.businessType || null,
        activity_category: input.activityCategory || null,
        email: input.email || null,
        phone: input.phone || null,
        whatsapp: input.whatsapp || input.phone || null,
        logo: input.logo || null,
        description: input.description || null,
        country: input.country || null,
        default_currency: currency,
        language: input.language || 'fr',
        status: 'active',
      })
      .select('id')
      .maybeSingle();
    if (oErr || !org) return { tenantId: tenant.id, organizationId: null };

    let addressId: number | null = null;
    if (input.address || input.city || input.country) {
      const { data: addr } = await supabase
        .from('core_addresses')
        .insert({
          tenant_id: tenant.id,
          owner_type: 'organization',
          owner_id: org.id,
          label: 'Adresse principale',
          address_line_1: input.address || null,
          city: input.city || null,
          region: input.region || null,
          country: input.country || null,
          is_default: true,
        })
        .select('id')
        .maybeSingle();
      addressId = (addr?.id as number) ?? null;
    }

    await supabase.from('core_locations').insert({
      tenant_id: tenant.id,
      organization_id: org.id,
      name: displayName,
      is_primary: true,
      address_id: addressId,
      phone: input.phone || null,
      manager_user_id: input.userId,
      status: 'active',
    });

    await supabase.from('core_memberships').insert({
      tenant_id: tenant.id,
      organization_id: org.id,
      user_id: input.userId,
      role: 'owner',
      role_label: 'Propriétaire',
      status: 'active',
      permissions: ['*'],
      is_primary: true,
    });

    return { tenantId: tenant.id, organizationId: org.id };
  } catch {
    return { tenantId: null, organizationId: null };
  }
}

/**
 * Rattache un utilisateur à l'organisation d'un commerce (membre du staff,
 * vendeur, etc.). Idempotent, best-effort.
 */
export async function linkUserToOrganization(
  userId: number,
  commerceId: number,
  role = 'employee',
  roleLabel = 'Membre',
): Promise<void> {
  try {
    if (!userId || !commerceId) return;
    const { data: org } = await supabase
      .from('core_organizations')
      .select('id, tenant_id')
      .eq('legacy_user_id', commerceId)
      .maybeSingle();
    if (!org) return;

    const { data: existing } = await supabase
      .from('core_memberships')
      .select('id')
      .eq('organization_id', org.id)
      .eq('user_id', userId)
      .maybeSingle();
    if (existing) return;

    await supabase.from('core_memberships').insert({
      tenant_id: org.tenant_id,
      organization_id: org.id,
      user_id: userId,
      role,
      role_label: roleLabel,
      status: 'active',
      permissions: role === 'owner' ? ['*'] : [],
    });
  } catch {
    /* best-effort */
  }
}

// ═════════════════════════════════════════════════════════════
// SOCLE PERSONNES — Contacts / Clients / Professionnels
// ═════════════════════════════════════════════════════════════

export interface CoreContact {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  contact_type: string;
  role_type: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  linked_user_id: number | null;
  notes: string | null;
  status: string;
  created_at: string | null;
}

export interface CoreCustomer {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  contact_id: number | null;
  display_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  customer_type: string;
  status: string;
  notes: string | null;
  created_at: string | null;
}

export interface CoreProfessional {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  display_name: string | null;
  profession: string | null;
  specialties: string | null;
  bio: string | null;
  languages: string | null;
  experience_years: number | null;
  certifications: string | null;
  verification_status: string;
  public_profile: boolean | null;
  status: string;
  created_at: string | null;
}

export interface CoreRole {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  role_key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  level: number;
  created_at: string | null;
  permissions?: string[];
}

export interface CoreDepartment {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  description: string | null;
  parent_department_id: number | null;
  manager_user_id: number | null;
  status: string;
  created_at: string | null;
}

export interface CoreEmployee {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  location_id: number | null;
  department_id: number | null;
  job_title: string | null;
  manager_user_id: number | null;
  email: string | null;
  phone: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
  commission_type: string | null;
  commission_value: number | null;
  sales_target: number | null;
  created_at: string | null;
}

export async function listCoreContacts(): Promise<CoreContact[]> {
  const { data, error } = await supabase.from('core_contacts').select('*').order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreContact[];
}

export async function listCoreCustomers(): Promise<CoreCustomer[]> {
  const { data, error } = await supabase.from('core_customers').select('*').order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreCustomer[];
}

export async function listCoreProfessionals(): Promise<CoreProfessional[]> {
  const { data, error } = await supabase.from('core_professionals').select('*').order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreProfessional[];
}

export async function listCoreRoles(): Promise<CoreRole[]> {
  const { data, error } = await supabase.from('core_roles').select('*').order('level', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreRole[];
}

export async function fetchRolePermissions(): Promise<Record<number, string[]>> {
  const { data, error } = await supabase.from('core_role_permissions').select('role_id, permission');
  if (error) return {};
  const map: Record<number, string[]> = {};
  (data || []).forEach((row) => {
    const r = row as { role_id: number; permission: string };
    if (!map[r.role_id]) map[r.role_id] = [];
    map[r.role_id].push(r.permission);
  });
  return map;
}

/** Rôles systèmes + rôles personnalisés d'une organisation, avec leurs permissions. */
export async function listRolesForOrganization(organizationId?: number | null): Promise<CoreRole[]> {
  const [roles, perms] = await Promise.all([listCoreRoles(), fetchRolePermissions()]);
  return roles
    .filter((r) => r.is_system || (organizationId != null && r.organization_id === organizationId))
    .map((r) => ({ ...r, permissions: perms[r.id] || [] }));
}

// ── Résolution de l'organisation du marchand ──

export async function getOrganizationByCommerce(commerceId: number): Promise<CoreOrganization | null> {
  const { data, error } = await supabase
    .from('core_organizations')
    .select('*')
    .eq('legacy_user_id', commerceId)
    .maybeSingle();
  if (error) throw error;
  return (data as CoreOrganization) || null;
}

export async function listOrganizationLocations(orgId: number): Promise<CoreLocation[]> {
  const { data, error } = await supabase
    .from('core_locations')
    .select('*, core_organizations(id, commercial_name), core_addresses(id, city, country, address_line_1, address_line_2, region, postal_code)')
    .eq('organization_id', orgId)
    .order('is_primary', { ascending: false })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as CoreLocation[];
}

export async function listOrganizationMemberships(orgId: number): Promise<CoreMembership[]> {
  const { data, error } = await supabase
    .from('core_memberships')
    .select('*, core_organizations(id, commercial_name)')
    .eq('organization_id', orgId)
    .order('is_primary', { ascending: false })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as CoreMembership[];
}

export async function listOrganizationContacts(orgId: number): Promise<CoreContact[]> {
  const { data, error } = await supabase.from('core_contacts').select('*').eq('organization_id', orgId).order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreContact[];
}

export async function listOrganizationCustomers(orgId: number): Promise<CoreCustomer[]> {
  const { data, error } = await supabase.from('core_customers').select('*').eq('organization_id', orgId).order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreCustomer[];
}

export async function listOrganizationProfessionals(orgId: number): Promise<CoreProfessional[]> {
  const { data, error } = await supabase.from('core_professionals').select('*').eq('organization_id', orgId).order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreProfessional[];
}

/** Recherche d'utilisateurs Zifek (pour rattacher un membre ou un client). */
export async function searchUsers(query: string): Promise<UserLabel[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from('users')
    .select('id, name, user_name, email')
    .or(`email.ilike.%${q}%,user_name.ilike.%${q}%,name.ilike.%${q}%`)
    .limit(12);
  if (error) return [];
  return (data || []) as UserLabel[];
}

// ── Mutations : établissements ──

export interface CoreAddressInput {
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
}

export interface LocationInput {
  tenantId: number | null;
  organizationId: number;
  name: string;
  phone?: string | null;
  managerUserId?: number | null;
  isPrimary?: boolean;
  address?: CoreAddressInput | null;
}

function hasAddressContent(a?: CoreAddressInput | null): boolean {
  if (!a) return false;
  return Boolean(a.line1 || a.line2 || a.city || a.region || a.postalCode || a.country);
}

export async function createCoreLocation(input: LocationInput): Promise<number | null> {
  let addressId: number | null = null;
  if (hasAddressContent(input.address)) {
    const a = input.address as CoreAddressInput;
    const { data } = await supabase
      .from('core_addresses')
      .insert({
        tenant_id: input.tenantId,
        owner_type: 'location',
        label: input.name,
        address_line_1: a.line1 || null,
        address_line_2: a.line2 || null,
        city: a.city || null,
        region: a.region || null,
        postal_code: a.postalCode || null,
        country: a.country || null,
      })
      .select('id')
      .maybeSingle();
    addressId = (data?.id as number) ?? null;
  }
  if (input.isPrimary) {
    await supabase.from('core_locations').update({ is_primary: false }).eq('organization_id', input.organizationId).eq('is_primary', true);
  }
  const { data, error } = await supabase
    .from('core_locations')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      phone: input.phone || null,
      manager_user_id: input.managerUserId || null,
      is_primary: !!input.isPrimary,
      address_id: addressId,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreLocation(id: number, input: LocationInput): Promise<void> {
  const { data: loc } = await supabase.from('core_locations').select('address_id').eq('id', id).maybeSingle();
  let addressId = (loc?.address_id as number) ?? null;
  if (hasAddressContent(input.address)) {
    const a = input.address as CoreAddressInput;
    const payload = {
      address_line_1: a.line1 || null,
      address_line_2: a.line2 || null,
      city: a.city || null,
      region: a.region || null,
      postal_code: a.postalCode || null,
      country: a.country || null,
    };
    if (addressId) {
      await supabase.from('core_addresses').update(payload).eq('id', addressId);
    } else {
      const { data } = await supabase
        .from('core_addresses')
        .insert({ tenant_id: input.tenantId, owner_type: 'location', label: input.name, ...payload })
        .select('id')
        .maybeSingle();
      addressId = (data?.id as number) ?? null;
    }
  }
  if (input.isPrimary) {
    await supabase.from('core_locations').update({ is_primary: false }).eq('organization_id', input.organizationId).eq('is_primary', true);
  }
  await supabase
    .from('core_locations')
    .update({
      name: input.name,
      phone: input.phone || null,
      manager_user_id: input.managerUserId || null,
      is_primary: !!input.isPrimary,
      address_id: addressId,
    })
    .eq('id', id);
}

export async function setCoreLocationStatus(id: number, status: string): Promise<void> {
  await supabase.from('core_locations').update({ status }).eq('id', id);
}

// ── Mutations : membres ──

export interface MembershipInput {
  tenantId: number | null;
  organizationId: number;
  userId: number;
  roleKey: string;
  roleLabel: string;
  roleId: number | null;
  permissions: string[];
}

export async function createCoreMembership(input: MembershipInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_memberships')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId,
      role: input.roleKey,
      role_label: input.roleLabel,
      role_id: input.roleId,
      status: 'active',
      permissions: input.permissions,
      joined_at: new Date().toISOString(),
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export interface MembershipPatch {
  roleKey?: string;
  roleLabel?: string;
  roleId?: number | null;
  permissions?: string[];
  status?: string;
}

export async function updateCoreMembership(id: number, patch: MembershipPatch): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.roleKey !== undefined) payload.role = patch.roleKey;
  if (patch.roleLabel !== undefined) payload.role_label = patch.roleLabel;
  if (patch.roleId !== undefined) payload.role_id = patch.roleId;
  if (patch.permissions !== undefined) payload.permissions = patch.permissions;
  if (patch.status !== undefined) payload.status = patch.status;
  if (Object.keys(payload).length === 0) return;
  await supabase.from('core_memberships').update(payload).eq('id', id);
}

// ── Mutations : rôles ──

export interface RoleInput {
  tenantId: number | null;
  organizationId: number | null;
  name: string;
  description?: string | null;
  permissions: string[];
}

export async function createCoreRole(input: RoleInput): Promise<number | null> {
  const key = slugify(input.name, `role-${Date.now()}`);
  const { data, error } = await supabase
    .from('core_roles')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      role_key: key,
      name: input.name,
      description: input.description || null,
      is_system: false,
      level: 50,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  const roleId = (data?.id as number) ?? null;
  if (roleId && input.permissions.length > 0) {
    await supabase.from('core_role_permissions').insert(input.permissions.map((p) => ({ role_id: roleId, permission: p })));
  }
  return roleId;
}

export async function setRolePermissions(roleId: number, perms: string[]): Promise<void> {
  await supabase.from('core_role_permissions').delete().eq('role_id', roleId);
  if (perms.length > 0) {
    await supabase.from('core_role_permissions').insert(perms.map((p) => ({ role_id: roleId, permission: p })));
  }
}

// ── Mutations : personnes ──

export interface ContactInput {
  tenantId: number | null;
  organizationId: number;
  contactType: string;
  roleType: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  jobTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  linkedUserId?: number | null;
  notes?: string | null;
}

export async function createCoreContact(input: ContactInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_contacts')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      contact_type: input.contactType,
      role_type: input.roleType,
      first_name: input.firstName || null,
      last_name: input.lastName || null,
      company_name: input.companyName || null,
      job_title: input.jobTitle || null,
      email: input.email || null,
      phone: input.phone || null,
      whatsapp: input.whatsapp || null,
      linked_user_id: input.linkedUserId || null,
      notes: input.notes || null,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export interface CustomerInput {
  tenantId: number | null;
  organizationId: number;
  displayName: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  customerType: string;
  userId?: number | null;
  notes?: string | null;
}

export async function createCoreCustomer(input: CustomerInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_customers')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId || null,
      display_name: input.displayName,
      company_name: input.companyName || null,
      email: input.email || null,
      phone: input.phone || null,
      customer_type: input.customerType,
      notes: input.notes || null,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export interface ProfessionalInput {
  tenantId: number | null;
  organizationId: number;
  displayName: string;
  profession?: string | null;
  specialties?: string | null;
  languages?: string | null;
  experienceYears?: number | null;
  bio?: string | null;
  certifications?: string | null;
  verificationStatus: string;
  publicProfile: boolean;
  userId?: number | null;
}

export async function createCoreProfessional(input: ProfessionalInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_professionals')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId || null,
      display_name: input.displayName,
      profession: input.profession || null,
      specialties: input.specialties || null,
      languages: input.languages || null,
      experience_years: input.experienceYears ?? null,
      bio: input.bio || null,
      certifications: input.certifications || null,
      verification_status: input.verificationStatus,
      public_profile: input.publicProfile,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

// ── Mutations : modification & activation / désactivation des personnes ──

export async function updateCoreContact(id: number, input: Partial<ContactInput>): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.contactType !== undefined) payload.contact_type = input.contactType;
  if (input.roleType !== undefined) payload.role_type = input.roleType;
  if (input.firstName !== undefined) payload.first_name = input.firstName || null;
  if (input.lastName !== undefined) payload.last_name = input.lastName || null;
  if (input.companyName !== undefined) payload.company_name = input.companyName || null;
  if (input.jobTitle !== undefined) payload.job_title = input.jobTitle || null;
  if (input.email !== undefined) payload.email = input.email || null;
  if (input.phone !== undefined) payload.phone = input.phone || null;
  if (input.whatsapp !== undefined) payload.whatsapp = input.whatsapp || null;
  if (input.linkedUserId !== undefined) payload.linked_user_id = input.linkedUserId || null;
  if (input.notes !== undefined) payload.notes = input.notes || null;
  const { error } = await supabase.from('core_contacts').update(payload).eq('id', id);
  if (error) throw error;
}

export async function updateCoreCustomer(id: number, input: Partial<CustomerInput>): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.displayName !== undefined) payload.display_name = input.displayName;
  if (input.companyName !== undefined) payload.company_name = input.companyName || null;
  if (input.email !== undefined) payload.email = input.email || null;
  if (input.phone !== undefined) payload.phone = input.phone || null;
  if (input.customerType !== undefined) payload.customer_type = input.customerType;
  if (input.userId !== undefined) payload.user_id = input.userId || null;
  if (input.notes !== undefined) payload.notes = input.notes || null;
  const { error } = await supabase.from('core_customers').update(payload).eq('id', id);
  if (error) throw error;
}

export async function updateCoreProfessional(id: number, input: Partial<ProfessionalInput>): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.displayName !== undefined) payload.display_name = input.displayName;
  if (input.profession !== undefined) payload.profession = input.profession || null;
  if (input.specialties !== undefined) payload.specialties = input.specialties || null;
  if (input.languages !== undefined) payload.languages = input.languages || null;
  if (input.experienceYears !== undefined) payload.experience_years = input.experienceYears ?? null;
  if (input.bio !== undefined) payload.bio = input.bio || null;
  if (input.certifications !== undefined) payload.certifications = input.certifications || null;
  if (input.verificationStatus !== undefined) payload.verification_status = input.verificationStatus;
  if (input.publicProfile !== undefined) payload.public_profile = input.publicProfile;
  const { error } = await supabase.from('core_professionals').update(payload).eq('id', id);
  if (error) throw error;
}

export type CorePersonTable = 'core_contacts' | 'core_customers' | 'core_professionals';

/** Active / désactive (status) une fiche personne du socle Core. */
export async function setCorePersonStatus(
  table: CorePersonTable,
  id: number,
  status: string,
): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ══════════════════════════════════════════════════════════
// FICHE CLIENT UNIQUE — liaison commandes & carnet de crédit
// ══════════════════════════════════════════════════════════

export interface EnsureCustomerInput {
  tenantId: number | null;
  organizationId: number;
  userId?: number | null;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  customerType?: string;
}

/**
 * Retrouve (ou crée) la fiche client unifiée d'une organisation. La
 * déduplication se fait par compte Zifek, puis email, puis téléphone, puis nom.
 */
export async function ensureCoreCustomer(input: EnsureCustomerInput): Promise<number | null> {
  try {
    const name = (input.displayName || '').trim();
    const email = (input.email || '').trim();
    const phone = (input.phone || '').trim();

    if (input.userId) {
      const { data } = await supabase
        .from('core_customers')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('user_id', input.userId)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (email) {
      const { data } = await supabase
        .from('core_customers')
        .select('id')
        .eq('organization_id', input.organizationId)
        .ilike('email', email)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (phone) {
      const { data } = await supabase
        .from('core_customers')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('phone', phone)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (name) {
      const { data } = await supabase
        .from('core_customers')
        .select('id')
        .eq('organization_id', input.organizationId)
        .ilike('display_name', name)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (!name && !email && !phone) return null;

    const { data: inserted, error } = await supabase
      .from('core_customers')
      .insert({
        tenant_id: input.tenantId,
        organization_id: input.organizationId,
        user_id: input.userId || null,
        display_name: name || email || phone,
        company_name: input.companyName || null,
        email: email || null,
        phone: phone || null,
        customer_type: input.customerType || 'individual',
        status: 'active',
      })
      .select('id')
      .maybeSingle();
    if (error) return null;
    const newId = (inserted?.id as number) ?? null;
    // Réindexation automatique du nouveau client (spec 43), best-effort.
    if (newId) {
      try {
        await reindexOne({
          tenantId: input.tenantId,
          organizationId: input.organizationId,
          entityType: 'customer',
          entityId: newId,
        });
      } catch {
        /* l'index ne doit jamais bloquer la création */
      }
    }
    return newId;
  } catch {
    return null;
  }
}

/**
 * Lie une fiche du carnet de crédit à la fiche client unifiée de sa
 * marchandise, et mémorise le lien (`core_customer_id`). Best-effort.
 */
export async function linkCreditCustomerToCore(cc: {
  id: number;
  merchant_id: number;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  linked_user_id?: number | null;
}): Promise<number | null> {
  try {
    const org = await getOrganizationByCommerce(cc.merchant_id);
    if (!org) return null;
    const coreId = await ensureCoreCustomer({
      tenantId: org.tenant_id,
      organizationId: org.id,
      userId: cc.linked_user_id ?? null,
      displayName: cc.full_name || '',
      email: cc.email ?? null,
      phone: cc.phone ?? null,
      customerType: 'individual',
    });
    if (!coreId) return null;
    await supabase.from('credit_customers').update({ core_customer_id: coreId }).eq('id', cc.id);
    return coreId;
  } catch {
    return null;
  }
}

/**
 * Rapproche TOUS les clients d'un marchand (carnet de crédit + commandes)
 * vers la fiche client unique. Idempotent, best-effort.
 */
export async function syncMerchantCustomersToCore(
  commerceId: number,
): Promise<{ credit: number; orders: number }> {
  const result = { credit: 0, orders: 0 };
  try {
    const org = await getOrganizationByCommerce(commerceId);
    if (!org) return result;

    const { data: creditRows } = await supabase
      .from('credit_customers')
      .select('id, full_name, phone, email, linked_user_id, core_customer_id')
      .eq('merchant_id', commerceId);
    for (const cc of (creditRows || []) as Array<Record<string, unknown>>) {
      const coreId = await ensureCoreCustomer({
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId: (cc.linked_user_id as number) ?? null,
        displayName: (cc.full_name as string) || '',
        email: (cc.email as string) ?? null,
        phone: (cc.phone as string) ?? null,
        customerType: 'individual',
      });
      if (coreId && cc.core_customer_id !== coreId) {
        await supabase.from('credit_customers').update({ core_customer_id: coreId }).eq('id', cc.id as number);
        result.credit += 1;
      }
    }

    const { data: orderRows } = await supabase
      .from('commande')
      .select('id, user_id, email, nomclient, nom, tel, core_customer_id')
      .eq('idvendeur', commerceId);
    for (const o of (orderRows || []) as Array<Record<string, unknown>>) {
      const rawUser = o.user_id != null ? String(o.user_id).trim() : '';
      const parsed = rawUser ? Number(rawUser) : NaN;
      const userId = Number.isFinite(parsed) ? parsed : null;
      const email = (o.email as string) || null;
      const name = (o.nomclient as string) || (o.nom as string) || '';
      if (!userId && !email && !name) continue;
      const coreId = await ensureCoreCustomer({
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId,
        displayName: name || email || '',
        email,
        phone: (o.tel as string) || null,
        customerType: 'individual',
      });
      if (coreId && o.core_customer_id !== coreId) {
        await supabase.from('commande').update({ core_customer_id: coreId }).eq('id', o.id as number);
        result.orders += 1;
      }
    }
  } catch {
    /* best-effort */
  }
  return result;
}

/** Statistiques de liaison : clients crédit reliés + nombre de commandes par fiche. */
export async function fetchCustomerLinkStats(commerceId: number): Promise<{
  creditLinked: number[];
  orderCounts: Record<number, number>;
}> {
  const creditLinked: number[] = [];
  const orderCounts: Record<number, number> = {};
  try {
    const { data: cc } = await supabase
      .from('credit_customers')
      .select('core_customer_id')
      .eq('merchant_id', commerceId)
      .not('core_customer_id', 'is', null);
    (cc || []).forEach((r) => {
      const v = (r as { core_customer_id: number | null }).core_customer_id;
      if (v != null) creditLinked.push(Number(v));
    });
  } catch {
    /* ignore */
  }
  try {
    const { data: ord } = await supabase
      .from('commande')
      .select('core_customer_id')
      .eq('idvendeur', commerceId)
      .not('core_customer_id', 'is', null);
    (ord || []).forEach((r) => {
      const v = (r as { core_customer_id: number | null }).core_customer_id;
      if (v != null) orderCounts[Number(v)] = (orderCounts[Number(v)] || 0) + 1;
    });
  } catch {
    /* ignore */
  }
  return { creditLinked, orderCounts };
}

/** Statistiques de liaison des contacts : fournisseurs & partenaires reliés. */
export async function fetchContactLinkStats(commerceId: number): Promise<{
  supplierLinked: number[];
  partnerLinked: number[];
}> {
  const supplierLinked: number[] = [];
  const partnerLinked: number[] = [];
  try {
    const { data: sup } = await supabase
      .from('finance_suppliers')
      .select('core_contact_id')
      .eq('merchant_id', commerceId)
      .not('core_contact_id', 'is', null);
    (sup || []).forEach((r) => {
      const v = (r as { core_contact_id: number | null }).core_contact_id;
      if (v != null) supplierLinked.push(Number(v));
    });
  } catch {
    /* ignore */
  }
  try {
    const { data: part } = await supabase
      .from('partenaires')
      .select('core_contact_id')
      .eq('idcommerce', commerceId)
      .not('core_contact_id', 'is', null);
    (part || []).forEach((r) => {
      const v = (r as { core_contact_id: number | null }).core_contact_id;
      if (v != null) partnerLinked.push(Number(v));
    });
  } catch {
    /* ignore */
  }
  return { supplierLinked, partnerLinked };
}

// ══════════════════════════════════════════════════════════
// DÉPARTEMENTS (une entreprise peut posséder plusieurs départements)
// ══════════════════════════════════════════════════════════

export async function listOrganizationDepartments(orgId: number): Promise<CoreDepartment[]> {
  const { data, error } = await supabase
    .from('core_departments')
    .select('*')
    .eq('organization_id', orgId)
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreDepartment[];
}

export interface DepartmentInput {
  tenantId: number | null;
  organizationId: number;
  name: string;
  description?: string | null;
  parentDepartmentId?: number | null;
  managerUserId?: number | null;
}

export async function createCoreDepartment(input: DepartmentInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_departments')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      description: input.description || null,
      parent_department_id: input.parentDepartmentId || null,
      manager_user_id: input.managerUserId || null,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreDepartment(id: number, input: Partial<DepartmentInput>): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.name !== undefined) payload.name = input.name;
  if (input.description !== undefined) payload.description = input.description || null;
  if (input.parentDepartmentId !== undefined) payload.parent_department_id = input.parentDepartmentId || null;
  if (input.managerUserId !== undefined) payload.manager_user_id = input.managerUserId || null;
  const { error } = await supabase.from('core_departments').update(payload).eq('id', id);
  if (error) throw error;
}

export async function setCoreDepartmentStatus(id: number, status: string): Promise<void> {
  const { error } = await supabase
    .from('core_departments')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ══════════════════════════════════════════════════════════
// EMPLOYÉS (la base RH — les fonctions avancées viendront de Zifek HR)
// ══════════════════════════════════════════════════════════

export async function listOrganizationEmployees(orgId: number): Promise<CoreEmployee[]> {
  const { data, error } = await supabase
    .from('core_employees')
    .select('*')
    .eq('organization_id', orgId)
    .order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreEmployee[];
}

export interface EmployeeInput {
  tenantId: number | null;
  organizationId: number;
  userId?: number | null;
  locationId?: number | null;
  departmentId?: number | null;
  jobTitle?: string | null;
  managerUserId?: number | null;
  email?: string | null;
  phone?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  commissionType?: string | null;
  commissionValue?: number | null;
  salesTarget?: number | null;
}

export async function createCoreEmployee(input: EmployeeInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_employees')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId || null,
      location_id: input.locationId || null,
      department_id: input.departmentId || null,
      job_title: input.jobTitle || null,
      manager_user_id: input.managerUserId || null,
      email: input.email || null,
      phone: input.phone || null,
      start_date: input.startDate || null,
      end_date: input.endDate || null,
      notes: input.notes || null,
      commission_type: input.commissionType || 'percentage',
      commission_value: input.commissionValue ?? 0,
      sales_target: input.salesTarget ?? 0,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreEmployee(id: number, input: Partial<EmployeeInput>): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.userId !== undefined) payload.user_id = input.userId || null;
  if (input.locationId !== undefined) payload.location_id = input.locationId || null;
  if (input.departmentId !== undefined) payload.department_id = input.departmentId || null;
  if (input.jobTitle !== undefined) payload.job_title = input.jobTitle || null;
  if (input.managerUserId !== undefined) payload.manager_user_id = input.managerUserId || null;
  if (input.email !== undefined) payload.email = input.email || null;
  if (input.phone !== undefined) payload.phone = input.phone || null;
  if (input.startDate !== undefined) payload.start_date = input.startDate || null;
  if (input.endDate !== undefined) payload.end_date = input.endDate || null;
  if (input.notes !== undefined) payload.notes = input.notes || null;
  if (input.commissionType !== undefined) payload.commission_type = input.commissionType || 'percentage';
  if (input.commissionValue !== undefined) payload.commission_value = input.commissionValue ?? 0;
  if (input.salesTarget !== undefined) payload.sales_target = input.salesTarget ?? 0;
  const { error } = await supabase.from('core_employees').update(payload).eq('id', id);
  if (error) throw error;
}

// ── Lectures globales (SuperAdmin) ──

export async function listAllCoreDepartments(): Promise<CoreDepartment[]> {
  const { data, error } = await supabase
    .from('core_departments')
    .select('*')
    .order('organization_id', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreDepartment[];
}

export async function listAllCoreEmployees(): Promise<CoreEmployee[]> {
  const { data, error } = await supabase
    .from('core_employees')
    .select('*')
    .order('organization_id', { ascending: true })
    .order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreEmployee[];
}

// ── Attribution des ventes / commissions par employé ──

export interface EmployeePerformance {
  employeeId: number;
  orders: number;
  sales: number;
  commission: number;
}

/** Attribue une commande à un employé (avec le montant de commission calculé). */
export async function attributeOrderToEmployee(
  orderId: number,
  coreEmployeeId: number | null,
  commissionAmount: number | null,
): Promise<void> {
  const { error } = await supabase
    .from('commande')
    .update({ core_employee_id: coreEmployeeId, commission_amount: commissionAmount })
    .eq('id', orderId);
  if (error) throw error;
}

/** Agrège les ventes & commissions par employé pour un commerce. */
export async function fetchEmployeePerformance(
  commerceId: number,
): Promise<Record<number, EmployeePerformance>> {
  const map: Record<number, EmployeePerformance> = {};
  try {
    const { data } = await supabase
      .from('commande')
      .select('id, totalttc, core_employee_id, commission_amount')
      .eq('idvendeur', commerceId)
      .not('core_employee_id', 'is', null);
    (data || []).forEach((row) => {
      const r = row as { totalttc: string | null; core_employee_id: number | null; commission_amount: number | null };
      const id = Number(r.core_employee_id);
      if (!id) return;
      if (!map[id]) map[id] = { employeeId: id, orders: 0, sales: 0, commission: 0 };
      map[id].orders += 1;
      map[id].sales += parseFloat(String(r.totalttc ?? '0')) || 0;
      map[id].commission += Number(r.commission_amount ?? 0) || 0;
    });
  } catch {
    /* best-effort */
  }
  return map;
}

export async function setCoreEmployeeStatus(id: number, status: string): Promise<void> {
  const { error } = await supabase
    .from('core_employees')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ══════════════════════════════════════════════════════════
// FICHE CONTACT UNIQUE — fournisseurs & partenaires
// Centralise tous les contacts (client / fournisseur / partenaire / …)
// dans le socle Personnes (core_contacts). Déduplication : compte Zifek,
// courriel, téléphone puis nom, dans le périmètre de l'organisation.
// ══════════════════════════════════════════════════════════

export interface EnsureContactInput {
  tenantId: number | null;
  organizationId: number;
  roleType: string;
  contactType?: string;
  displayName: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  linkedUserId?: number | null;
  jobTitle?: string | null;
  notes?: string | null;
}

/** Retrouve (ou crée) la fiche contact unifiée d'une organisation. Best-effort. */
export async function ensureCoreContact(input: EnsureContactInput): Promise<number | null> {
  try {
    const name = (input.displayName || '').trim();
    const email = (input.email || '').trim();
    const phone = (input.phone || '').trim();

    if (input.linkedUserId) {
      const { data } = await supabase
        .from('core_contacts')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('role_type', input.roleType)
        .eq('linked_user_id', input.linkedUserId)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (email) {
      const { data } = await supabase
        .from('core_contacts')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('role_type', input.roleType)
        .ilike('email', email)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (phone) {
      const { data } = await supabase
        .from('core_contacts')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('role_type', input.roleType)
        .eq('phone', phone)
        .maybeSingle();
      if (data?.id) return data.id as number;
    }
    if (name) {
      const { data } = await supabase
        .from('core_contacts')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('role_type', input.roleType)
        .ilike('company_name', name)
        .maybeSingle();
      if (data?.id) return data.id as number;
      const { data: d2 } = await supabase
        .from('core_contacts')
        .select('id')
        .eq('organization_id', input.organizationId)
        .eq('role_type', input.roleType)
        .ilike('first_name', name)
        .maybeSingle();
      if (d2?.id) return d2.id as number;
    }
    if (!name && !email && !phone) return null;

    const contactType = input.contactType || (input.companyName ? 'company' : 'person');
    const { data, error } = await supabase
      .from('core_contacts')
      .insert({
        tenant_id: input.tenantId,
        organization_id: input.organizationId,
        contact_type: contactType,
        role_type: input.roleType,
        company_name: contactType === 'company' ? (input.companyName || name) : (input.companyName || null),
        first_name: contactType === 'person' ? name : null,
        job_title: input.jobTitle || null,
        email: email || null,
        phone: phone || null,
        whatsapp: input.whatsapp || null,
        linked_user_id: input.linkedUserId || null,
        notes: input.notes || null,
        status: 'active',
      })
      .select('id')
      .maybeSingle();
    if (error) return null;
    return (data?.id as number) ?? null;
  } catch {
    return null;
  }
}

/** Relie un fournisseur (Finance) à la fiche contact unique. Best-effort. */
export async function linkSupplierToCore(s: {
  id: number;
  merchant_id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
}): Promise<number | null> {
  try {
    const org = await getOrganizationByCommerce(s.merchant_id);
    if (!org) return null;
    const contactId = await ensureCoreContact({
      tenantId: org.tenant_id,
      organizationId: org.id,
      roleType: 'supplier',
      contactType: 'company',
      displayName: s.name || '',
      companyName: s.name || null,
      email: s.email ?? null,
      phone: s.phone ?? null,
      notes: s.notes ?? null,
    });
    if (!contactId) return null;
    await supabase.from('finance_suppliers').update({ core_contact_id: contactId }).eq('id', s.id);
    return contactId;
  } catch {
    return null;
  }
}

/** Relie un partenaire (boutique) à la fiche contact unique. Best-effort. */
export async function linkPartnerToCore(p: {
  id: number;
  idcommerce: number;
  titre: string;
  description?: string | null;
}): Promise<number | null> {
  try {
    const org = await getOrganizationByCommerce(p.idcommerce);
    if (!org) return null;
    const contactId = await ensureCoreContact({
      tenantId: org.tenant_id,
      organizationId: org.id,
      roleType: 'partner',
      contactType: 'company',
      displayName: p.titre || '',
      companyName: p.titre || null,
      notes: p.description ?? null,
    });
    if (!contactId) return null;
    await supabase.from('partenaires').update({ core_contact_id: contactId }).eq('id', p.id);
    return contactId;
  } catch {
    return null;
  }
}

/**
 * Rapproche TOUS les fournisseurs et partenaires d'un marchand vers la fiche
 * contact unique. Idempotent, best-effort.
 */
export async function syncMerchantPeopleToCore(
  commerceId: number,
): Promise<{ suppliers: number; partners: number }> {
  const result = { suppliers: 0, partners: 0 };
  try {
    const org = await getOrganizationByCommerce(commerceId);
    if (!org) return result;

    const { data: supRows } = await supabase
      .from('finance_suppliers')
      .select('id, merchant_id, name, phone, email, notes, core_contact_id')
      .eq('merchant_id', commerceId);
    for (const s of (supRows || []) as Array<{
      id: number; merchant_id: number; name: string;
      phone: string | null; email: string | null; notes: string | null; core_contact_id: number | null;
    }>) {
      const id = await linkSupplierToCore(s);
      if (id && s.core_contact_id !== id) result.suppliers += 1;
    }

    const { data: partRows } = await supabase
      .from('partenaires')
      .select('id, idcommerce, titre, description, core_contact_id')
      .eq('idcommerce', commerceId);
    for (const p of (partRows || []) as Array<{
      id: number; idcommerce: number; titre: string; description: string | null; core_contact_id: number | null;
    }>) {
      const id = await linkPartnerToCore(p);
      if (id && p.core_contact_id !== id) result.partners += 1;
    }
  } catch {
    /* best-effort */
  }
  return result;
}